import PropTypes from 'prop-types';
import { memo } from 'react';
import {
  FaBan,
  FaChild,
  FaCommentDots,
  FaRegBuilding,
  FaRegCalendarTimes,
  FaRegCheckCircle,
  FaRegQuestionCircle,
  FaRegSun,
  FaShuttleVan,
  FaTimes,
  FaUserFriends,
  FaUtensils,
  FaWalking,
} from 'react-icons/fa';
const Block = ({ title, icon: Icon, children }) => (
  <div className="mb-4 flex">
    <Icon className="mr-2 mt-1 text-gray-600 flex-shrink-0" size="16" />
    <div className="min-w-0">
      <div className="text-base font-semibold">{title}</div>
      <div className="text-sm text-gray-700 text-justify">{children}</div>
    </div>
  </div>
);

Block.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
};

const Lines = ({ text }) => (
  <>
    {text
      .split(/\r?\n/)
      .map((line) => line.replace(/^\s*[-•*]\s*/, '').trim())
      .filter(Boolean)
      .map((line) => (
        <div key={line} className="leading-snug">
          {line}
        </div>
      ))}
  </>
);

Lines.propTypes = {
  text: PropTypes.string.isRequired,
};

const GoodToKnowDetails = ({ itinerary, product }) => {
  const notIncluded = product?.notIncluded;
  const restrictions = product?.restrictions;
  const recommendations = product?.recommendations;
  const languages = (product?.languages || []).join(', ');
  const meals = [product?.foodIncluded, product?.drinkIncluded].filter(Boolean).join(' · ');
  const groupSize =
    product?.minQuantity || product?.maxQuantity
      ? `${product?.minQuantity ? `Minimum ${product.minQuantity}` : ''}${
          product?.minQuantity && product?.maxQuantity ? ', ' : ''
        }${product?.maxQuantity ? `maximum ${product.maxQuantity}` : ''} guests`
      : null;
  const cancellation = itinerary.cancellationPolicy;

  return (
    <div className="text-sm grid md:grid-cols-2 gap-x-8">
      <div>
        <div className="text-lg md:text-xl font-semibold font-subHeading mb-3">Inclusions</div>
        {languages && (
          <Block title="Languages" icon={FaCommentDots}>
            {languages}
          </Block>
        )}
        {meals && (
          <Block title="Food and drink" icon={FaUtensils}>
            {meals}
          </Block>
        )}
        {product?.hotelPickupOffered && (
          <Block title="Hotel pick-up" icon={FaShuttleVan}>
            Offered on this tour
          </Block>
        )}
        {notIncluded && (
          <Block title="Not included" icon={FaTimes}>
            <Lines text={notIncluded} />
          </Block>
        )}
      </div>

      <div>
        <div className="text-lg md:text-xl font-semibold font-subHeading mb-3">Requirements</div>
        {product?.skillLevel && (
          <Block title="Difficulty" icon={FaWalking}>
            {product.skillLevel}
          </Block>
        )}
        {recommendations && (
          <Block title="Recommendations" icon={FaRegSun}>
            <Lines text={recommendations} />
          </Block>
        )}
        {groupSize && (
          <Block title="Group size" icon={FaUserFriends}>
            {groupSize}
          </Block>
        )}
        {product?.minimumAge != null && (
          <Block title="Minimum age" icon={FaChild}>{`${product.minimumAge} years`}</Block>
        )}
        {restrictions && (
          <Block title="Restrictions" icon={FaBan}>
            <Lines text={restrictions} />
          </Block>
        )}
      </div>

      <div className="md:col-span-2 border-t pt-4">
        {(product?.supplierName || itinerary.tour?.supplierName) && (
          <Block title="Operated by" icon={FaRegBuilding}>
            {product?.supplierName || itinerary.tour?.supplierName}
          </Block>
        )}
        {cancellation && (
          <Block title="Cancellation policy" icon={FaRegCalendarTimes}>
            {cancellation}
          </Block>
        )}
        {itinerary.tour?.confirmationType && (
          <Block title="Confirmation" icon={FaRegCheckCircle}>
            {itinerary.tour.confirmationType}
          </Block>
        )}
        {product?.areBookingQuestionsMandatory && product?.bookingQuestions && (
          <Block title="The operator needs an answer to book" icon={FaRegQuestionCircle}>
            {product.bookingQuestions}
          </Block>
        )}
      </div>
    </div>
  );
};

GoodToKnowDetails.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  product: PropTypes.shape(),
};

GoodToKnowDetails.defaultProps = {
  product: undefined,
};

export default memo(GoodToKnowDetails);
